import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button, Empty, Input, Modal, Radio, Spin, Table, Tag, Timeline, Tooltip, message,
} from "antd";
import {
  CloudUploadOutlined, FilePdfOutlined, GoogleOutlined, HistoryOutlined,
  SearchOutlined, SettingOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import calendar from "dayjs/plugin/calendar";
import styled from "styled-components";

import { presentationsApi } from "../../../api/presentationsApi";
import {
  DRIVE_STATE_COLORS,
  DRIVE_STATE_LABELS,
  IMonitorRow,
  IPresentationEvent,
  NAMESPACE_LABELS,
  STATE_COLORS,
} from "../../../types/models/presentations";

dayjs.extend(calendar);

/**
 * Every presentation of every active template, and where Drive stands.
 *
 * Generation and publishing are automatic, so this is where someone checks what
 * the system did — which brochures are on Drive, which are waiting, which failed
 * and why — and then compares it with the folder itself. Anything beyond that
 * (re-render, photos, map, floor plan, copy) opens the template's drawer at
 * that property's row, so there is one place those actions live, not two.
 */

type Filter = "sending" | "attention" | "all";

const Body = styled.div`
  .toolbar {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 16px;
  }

  .sub {
    display: block;
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }

  .row-actions {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .row-actions .anticon {
    font-size: 18px;
  }
`;

/** Sending: the template publishes and the folder is confirmed. */
export const isSending = (row: IMonitorRow) => row.drive.state !== "off";

export const needsAttention = (row: IMonitorRow) =>
  row.status === "failed" || row.status === "needs_info" || row.drive.state === "failed";

const matches = (row: IMonitorRow, filter: Filter, query: string) => {
  if (filter === "sending" && !isSending(row)) return false;
  if (filter === "attention" && !needsAttention(row)) return false;
  const q = query.trim().toLowerCase();
  return !q || row.listing_name.toLowerCase().includes(q)
    || row.template.name.toLowerCase().includes(q);
};

const when = (at: string | null) => {
  if (!at) return <span className="sub">—</span>;
  return (
    <Tooltip title={dayjs(at).format("D MMM YYYY, HH:mm")}>
      <span>{dayjs(at).calendar()}</span>
    </Tooltip>
  );
};

const eventText = (event: IPresentationEvent) => {
  if (event.kind === "sent") {
    const how = { created: "created", updated: "updated", took_over: "took over" }[
      event.detail.action as string] ?? event.detail.action;
    return `${event.kind_label}: ${how} ${event.detail.file_name ?? ""}`;
  }
  if (event.kind === "changed") {
    const changes: { path: string; old: string; new: string }[] = event.detail.changes ?? [];
    const shown = changes.slice(0, 3).map((change) => (
      change.path.startsWith("image_")
        ? `${change.path} photo`
        : `${change.path.split(".").slice(-2).join(" ")} ${change.old || "—"} → ${change.new || "—"}`
    ));
    const more = changes.length > 3 ? ` and ${changes.length - 3} more` : "";
    const missing = event.detail.missing_copy?.length
      ? ` (${event.detail.missing_copy.join(", ")})` : "";
    return `${shown.join("; ")}${more} · ${event.detail.action}${missing}`;
  }
  if (event.detail.error) return `${event.kind_label}: ${event.detail.error}`;
  return event.kind_label;
};

const EVENT_COLORS: Record<IPresentationEvent["kind"], string> = {
  rendered: "blue",
  render_failed: "red",
  sent: "green",
  send_failed: "red",
  changed: "gray",
};

export interface PresentationMonitorProps {
  /** Show a brochure in the page's PDF viewer. */
  onViewPdf: (title: string, url: string, downloadUrl?: string | null) => void;
  /** Open the template's drawer at this property's row. */
  onManage: (templateId: number, listingName: string) => void;
}

const PresentationMonitor = ({ onViewPdf, onManage }: PresentationMonitorProps) => {
  const [rows, setRows] = useState<IMonitorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>("sending");
  const [query, setQuery] = useState("");
  const [history, setHistory] = useState<{ row: IMonitorRow; events?: IPresentationEvent[] }>();
  const poll = useRef<number>();

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setRows(await presentationsApi.monitor());
    } catch (e) {
      if (!quiet) message.error("Could not load presentations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Renders and sends run on the worker, so refresh while anything is moving and
  // stop once nothing is.
  useEffect(() => {
    const moving = rows.some(
      (row) => row.status === "pending" || row.drive.state === "waiting");
    window.clearTimeout(poll.current);
    if (moving) poll.current = window.setTimeout(() => load(true), 10000);
    return () => window.clearTimeout(poll.current);
  }, [rows, load]);

  const openHistory = async (row: IMonitorRow) => {
    setHistory({ row });
    try {
      setHistory({ row, events: await presentationsApi.events(row.id) });
    } catch (e) {
      message.error("Could not load the history.");
      setHistory(undefined);
    }
  };

  const sendNow = async (row: IMonitorRow) => {
    try {
      await presentationsApi.sendToDrive(row.id);
      message.success(`Queued ${row.listing_name} for Drive.`);
      load(true);
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? "Could not queue that send.");
    }
  };

  const visible = rows.filter((row) => matches(row, filter, query));
  const counts = {
    sending: rows.filter(isSending).length,
    attention: rows.filter(needsAttention).length,
    all: rows.length,
  };

  const columns = [
    {
      title: "Property",
      key: "listing",
      render: (_: any, row: IMonitorRow) => row.listing_name,
      sorter: (a: IMonitorRow, b: IMonitorRow) => a.listing_name.localeCompare(b.listing_name),
    },
    {
      title: "Template",
      key: "template",
      render: (_: any, row: IMonitorRow) => (
        <>
          {row.template.name} <Tag>{row.template.language.toUpperCase()}</Tag>
          <span className="sub">
            {row.is_teaser
              ? "Teaser"
              : NAMESPACE_LABELS[row.template.namespace] ?? row.template.namespace}
          </span>
        </>
      ),
    },
    {
      title: "Rendered",
      key: "rendered",
      render: (_: any, row: IMonitorRow) => (
        <>
          <Tooltip title={row.error}>
            <Tag color={STATE_COLORS[row.status]}>{row.status_label}</Tag>
          </Tooltip>
          <span className="sub">{when(row.generated_at)}</span>
        </>
      ),
    },
    {
      title: "Drive",
      key: "drive",
      render: (_: any, row: IMonitorRow) => (
        <>
          <Tooltip title={row.drive.reason}>
            <Tag color={DRIVE_STATE_COLORS[row.drive.state]}>
              {DRIVE_STATE_LABELS[row.drive.state]}
            </Tag>
          </Tooltip>
          <span className="sub">
            {row.drive.folder_name
              ? `${row.drive.folder_name}${row.drive.folder_status === "confirmed" ? "" : ` (${row.drive.folder_status})`}`
              : "No folder yet"}
          </span>
        </>
      ),
    },
    {
      title: "Last sent",
      key: "synced",
      render: (_: any, row: IMonitorRow) => when(row.drive.synced_at),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, row: IMonitorRow) => (
        <div className="row-actions">
          {row.pdf_url && (
            <Tooltip title="Preview PDF">
              <a
                aria-label={`preview ${row.id}`}
                onClick={() => onViewPdf(
                  row.is_teaser ? `${row.listing_name} — teaser` : row.listing_name,
                  row.pdf_url as string, row.pptx_url)}
              >
                <FilePdfOutlined />
              </a>
            </Tooltip>
          )}
          {row.drive.link && (
            <Tooltip title={`Open ${row.drive.file_name} in Drive`}>
              <a href={row.drive.link} target="_blank" rel="noreferrer">
                <GoogleOutlined />
              </a>
            </Tooltip>
          )}
          {(row.drive.state === "waiting" || row.drive.state === "failed") && (
            <Tooltip title="Send to Drive now">
              <a aria-label={`send ${row.id}`} onClick={() => sendNow(row)}>
                <CloudUploadOutlined />
              </a>
            </Tooltip>
          )}
          <Tooltip title="History">
            <a aria-label={`history ${row.id}`} onClick={() => openHistory(row)}>
              <HistoryOutlined />
            </a>
          </Tooltip>
          <Tooltip title="Re-render, photos, map, floor plan, copy">
            <a
              aria-label={`manage ${row.id}`}
              onClick={() => onManage(row.template.id, row.listing_name)}
            >
              <SettingOutlined />
            </a>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <Body>
      <div className="toolbar">
        <Radio.Group
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          optionType="button"
        >
          <Radio.Button value="sending">Sending ({counts.sending})</Radio.Button>
          <Radio.Button value="attention">Needs attention ({counts.attention})</Radio.Button>
          <Radio.Button value="all">All ({counts.all})</Radio.Button>
        </Radio.Group>
        <Input
          allowClear
          style={{ maxWidth: 280 }}
          prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.3)" }} />}
          placeholder="Property or template"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button onClick={() => load()}>Refresh</Button>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: "center" }}><Spin /></div>
      ) : visible.length === 0 ? (
        <Empty
          description={filter === "sending"
            ? "Nothing is sending yet — confirm folders in the Drive folders tab, and turn on “Send PDFs to Drive” on a template."
            : "Nothing here."}
        />
      ) : (
        <Table
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={visible}
          pagination={{ pageSize: 50, hideOnSinglePage: true }}
        />
      )}

      <Modal
        visible={!!history}
        onCancel={() => setHistory(undefined)}
        footer={null}
        title={history
          ? `${history.row.listing_name} — ${history.row.template.name} (${history.row.template.language.toUpperCase()})`
          : ""}
      >
        {!history?.events ? <Spin /> : history.events.length === 0 ? (
          <Empty description="Nothing recorded yet." />
        ) : (
          <Timeline>
            {history.events.map((event) => (
              <Timeline.Item key={event.id} color={EVENT_COLORS[event.kind]}>
                <div>{eventText(event)}</div>
                <span className="sub" style={{ color: "rgba(0,0,0,.45)", fontSize: 12 }}>
                  {dayjs(event.created_at).format("D MMM YYYY, HH:mm")}
                </span>
                {event.detail.link && (
                  <div>
                    <a href={event.detail.link} target="_blank" rel="noreferrer">
                      Open in Drive
                    </a>
                  </div>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
        )}
      </Modal>
    </Body>
  );
};

export default PresentationMonitor;
